import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Users } from 'lucide-react';
import type { SystemUser } from '@bliks-io/contracts';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  getColorSchemeFromString,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  UserCard,
  DataTable,
  createColumnHeader,
  type DataTableColumnDef
} from '@bliks-io/ui';

interface SystemUsersCardProps {
  users: SystemUser[];
}

export function SystemUsersCard({ users }: SystemUsersCardProps) {
  const { t } = useTranslation();
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Extract unique roles for the filter dropdown
  const uniqueRoles = useMemo(() => {
    const roles = new Map<string, string>();
    users.forEach((user) => {
      if (user.role) {
        roles.set(user.role._id, user.role.name);
      }
    });
    return Array.from(roles.entries()).map(([id, name]) => ({ id, name }));
  }, [users]);

  // Filter users by role and search query
  const filteredUsers = useMemo(() => {
    let result = users;

    if (roleFilter !== 'all') {
      result = result.filter((user) => user.role?._id === roleFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((user) => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        return fullName.includes(query);
      });
    }

    return result;
  }, [users, roleFilter, searchQuery]);

  // Get translated involvement type label
  const getInvolvementTypeLabel = useCallback(
    (type: string) => {
      switch (type) {
        case 'owner':
          return t('organization.systems.systemUsers.involvementType.owner');
        case 'helper':
          return t('organization.systems.systemUsers.involvementType.helper');
        case 'whoElseDoesThis':
          return t('organization.systems.systemUsers.involvementType.whoElseDoesThis');
        default:
          return type;
      }
    },
    [t]
  );

  // Get badge variant for involvement type
  const getInvolvementTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'owner':
        return 'default' as const;
      case 'helper':
        return 'secondary' as const;
      case 'whoElseDoesThis':
        return 'outline' as const;
      default:
        return 'outline' as const;
    }
  };

  // Column definitions for system users table
  const columns: DataTableColumnDef<SystemUser>[] = useMemo(
    () => [
      {
        accessorKey: 'lastName',
        header: createColumnHeader(t('organization.systems.systemUsers.user')),
        cell: ({ row }) => (
          <UserCard
            user={{
              firstName: row.original.firstName,
              lastName: row.original.lastName,
              email: row.original.email,
              avatar: row.original.avatar,
              departmentAbbreviation: row.original.department?.abbreviation
            }}
            variant="name-only"
            size="sm"
          />
        ),
        filterFn: (row, _columnId, filterValue) => {
          const fullName = `${row.original.firstName} ${row.original.lastName}`.toLowerCase();
          return fullName.includes((filterValue as string).toLowerCase());
        }
      },
      {
        accessorKey: 'role',
        header: createColumnHeader(t('organization.systems.systemUsers.role')),
        cell: ({ row }) =>
          row.original.role ? (
            <Badge shape="rounded" colorScheme={getColorSchemeFromString(row.original.role._id)}>
              {row.original.role.name}
            </Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
        meta: { fitContent: true }
      },
      {
        id: 'involvedProcesses',
        header: createColumnHeader(t('organization.systems.systemUsers.involvedProcesses')),
        cell: ({ row }) => {
          const processCount = row.original.totalProcesses;

          if (processCount === 0) {
            return <span className="text-muted-foreground">-</span>;
          }

          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge shape="circle" colorScheme="gray" className="cursor-pointer">
                    {processCount}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <div className="space-y-2">
                    {row.original.processInvolvements.map((pi) => (
                      <div key={pi.processId} className="flex flex-col gap-1">
                        <div className="font-medium">{pi.processName}</div>
                        <div className="flex flex-wrap gap-1">
                          {pi.involvementTypes.map((type) => (
                            <Badge
                              key={type}
                              variant={getInvolvementTypeBadgeVariant(type)}
                              className="text-xs"
                            >
                              {getInvolvementTypeLabel(type)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
        meta: { fitContent: true }
      }
    ],
    [t, getInvolvementTypeLabel]
  );

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          {t('organization.systems.systemUsers.title')}
        </CardTitle>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('organization.systems.systemUsers.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full sm:w-[200px]"
            />
          </div>
          {uniqueRoles.length > 0 && (
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder={t('organization.systems.systemUsers.filterByRole')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('organization.systems.systemUsers.allRoles')}
                </SelectItem>
                {uniqueRoles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          data={filteredUsers}
          emptyMessage={t('organization.systems.systemUsers.noUsers')}
          enablePagination
          initialPageSize={5}
          pageSizeOptions={[5, 10, 20, 50]}
        />
      </CardContent>
    </Card>
  );
}
